import AbstractDashboardWidget from './abstractDashboardWidget';
import { ConstructorInput } from './dashboardWidgetFactory';
import { getPropertyValue, checkNonEmptyString } from '@/utils/typeChecks';

const ns = 'app.services.imageUploadDashboardWidget';

export const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/svg+xml'] as const;

export type Configurations = {
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
  uploadedAt?: string;
  linkUrl?: string;
};

function assert(val: unknown): Configurations {
  if (typeof val !== 'object' || val === null) {
    throw new Error(`${ns}.getConfigurations: configurations must be an object`);
  }

  const fileUrl = getPropertyValue(val, 'fileUrl');
  const fileName = getPropertyValue(val, 'fileName');
  const fileType = getPropertyValue(val, 'fileType');
  const uploadedAt = getPropertyValue(val, 'uploadedAt');
  const linkUrl = getPropertyValue(val, 'linkUrl');

  // All fields are optional, but if they exist, they must be strings
  if (fileUrl !== undefined && !checkNonEmptyString(fileUrl)) {
    throw new Error(`${ns}.getConfigurations: fileUrl must be a non-empty string`);
  }

  if (fileName !== undefined && !checkNonEmptyString(fileName)) {
    throw new Error(`${ns}.getConfigurations: fileName must be a non-empty string`);
  }

  if (fileType !== undefined && !checkNonEmptyString(fileType)) {
    throw new Error(`${ns}.getConfigurations: fileType must be a non-empty string`);
  }

  if (uploadedAt !== undefined && !checkNonEmptyString(uploadedAt)) {
    throw new Error(`${ns}.getConfigurations: uploadedAt must be a non-empty string`);
  }

  if (linkUrl !== undefined && !checkNonEmptyString(linkUrl)) {
    throw new Error(`${ns}.getConfigurations: linkUrl must be a non-empty string`);
  }

  return val as Configurations;
}

export default class ImageUploadDashboardWidget extends AbstractDashboardWidget {
  #configurationsString: string;
  #configurations: Configurations | undefined;

  constructor(input: ConstructorInput) {
    super(input);
    this.#configurationsString = input.configurations;
  }

  getConfigurations(): Configurations {
    if (!this.#configurations) {
      this.#configurations = assert(JSON.parse(this.#configurationsString));
    }
    return this.#configurations;
  }

  setConfigurations(c: Configurations): void {
    this.#configurations = assert(c);
  }
}
